import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/contexts/AuthContext";
import { Globe, Check, Copy, Loader2, X, Code2 } from "lucide-react";

export const WebchatCard = ({ existingInbox, onConnected, onDisconnect, lang }) => {
  const t = (ar, en) => (lang === "ar" ? ar : en);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", website_url: "", welcome_title: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [embed, setEmbed] = useState("");
  const [embedLoading, setEmbedLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyEmbed = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) { /* ignore */ }
  };

  const submit = async () => {
    setError("");
    if (!form.website_url.trim()) { setError(t("أدخل رابط موقعك", "Enter your website URL")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/me/channels/webchat/connect", {
        website_url: form.website_url,
        name: form.name || "Website",
        welcome_title: form.welcome_title,
      });
      setEmbed(data.embed_code || "");
      setOpen(false);
      await onConnected(t("🎉 تم إنشاء ودجة الموقع! انسخ الكود وضعه في موقعك.", "🎉 Website widget created! Copy the code into your site."));
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const loadEmbed = async () => {
    if (!existingInbox) return;
    setEmbedLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/me/channels/webchat/${existingInbox.id}/embed`);
      setEmbed(data.embed_code || "");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setEmbedLoading(false); }
  };

  return (
    <Card data-testid="webchat-card" className="rounded-3xl border-stone-200 bg-white">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-stone-800 flex items-center justify-center text-white flex-shrink-0">
            <Globe size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-heading font-bold text-stone-900">{t("محادثة الموقع (Webchat)", "Website Chat (Webchat)")}</p>
              {existingInbox ? (
                <Badge data-testid="webchat-connected-badge" className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">
                  <Check size={11} className="me-1" />{t("مربوط", "Connected")}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] border-stone-200 text-stone-500">{t("غير مربوط", "Not connected")}</Badge>
              )}
            </div>
            <p className="text-sm text-stone-500 mt-1">
              {existingInbox
                ? t(`الودجة نشطة${existingInbox.website_url ? ` على ${existingInbox.website_url}` : ""}. انسخ كود التضمين لأي صفحة.`, "Widget is active. Copy the embed code into any page.")
                : t("أضف نافذة دردشة لموقعك بخطوة واحدة — الرسائل تصل صندوقك الموحد مباشرة.", "Add a chat bubble to your website in one step — messages land in your unified inbox.")}
            </p>

            {error && (
              <div data-testid="webchat-error" className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-start gap-2">
                <X size={14} className="mt-0.5 flex-shrink-0" />{error}
              </div>
            )}

            {!existingInbox && open && (
              <div data-testid="webchat-form" className="mt-4 space-y-3 bg-stone-50 rounded-2xl p-4 border border-stone-100">
                <Input data-testid="webchat-name-input" placeholder={t("اسم الموقع (مثال: متجر أحمد)", "Site name (e.g. Ahmed Store)")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-white" />
                <Input data-testid="webchat-url-input" dir="ltr" placeholder="https://mystore.com" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="rounded-xl bg-white" />
                <Input data-testid="webchat-welcome-input" placeholder={t("رسالة الترحيب (اختياري)", "Welcome message (optional)")} value={form.welcome_title} onChange={(e) => setForm({ ...form, welcome_title: e.target.value })} className="rounded-xl bg-white" />
                <div className="flex gap-2">
                  <Button data-testid="webchat-submit-btn" onClick={submit} disabled={saving} className="rounded-xl bg-stone-800 hover:bg-stone-900 text-white">
                    {saving ? <Loader2 size={15} className="animate-spin me-2" /> : <Check size={15} className="me-2" />}
                    {t("إنشاء الودجة", "Create widget")}
                  </Button>
                  <Button data-testid="webchat-cancel-btn" variant="ghost" onClick={() => setOpen(false)} className="rounded-xl">{t("إلغاء", "Cancel")}</Button>
                </div>
              </div>
            )}

            {embed && (
              <div data-testid="webchat-embed-block" className="mt-4 bg-stone-900 rounded-2xl p-4 relative">
                <p className="text-[11px] text-stone-400 mb-2">{t("الصق هذا الكود قبل وسم </body> في موقعك:", "Paste this code before the </body> tag on your site:")}</p>
                <pre dir="ltr" className="text-[11px] text-emerald-300 overflow-x-auto whitespace-pre-wrap break-all max-h-48">{embed}</pre>
                <Button data-testid="webchat-copy-btn" size="sm" onClick={() => copyEmbed(embed)} className="absolute top-3 end-3 rounded-lg bg-stone-700 hover:bg-stone-600 text-white h-8">
                  {copied ? <Check size={13} className="me-1" /> : <Copy size={13} className="me-1" />}
                  {copied ? t("نُسخ!", "Copied!") : t("نسخ", "Copy")}
                </Button>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0">
            {!existingInbox && !open && (
              <Button data-testid="webchat-open-form-btn" onClick={() => setOpen(true)} className="rounded-xl bg-stone-800 hover:bg-stone-900 text-white">
                <Globe size={15} className="me-2" />{t("اربط موقعك", "Connect site")}
              </Button>
            )}
            {existingInbox && (
              <>
                <Button data-testid="webchat-show-embed-btn" variant="outline" onClick={loadEmbed} disabled={embedLoading} className="rounded-xl">
                  {embedLoading ? <Loader2 size={14} className="animate-spin me-2" /> : <Code2 size={14} className="me-2" />}
                  {t("كود التضمين", "Embed code")}
                </Button>
                <Button data-testid="webchat-disconnect-btn" variant="ghost" onClick={() => onDisconnect(existingInbox.id)} className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50">
                  {t("فصل", "Disconnect")}
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
